import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ScriptableChartContext,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
    Divider,
    Flex,
    Image,
    SegmentedControl,
    Text,
    Tooltip as MantineTooltip,
} from '@mantine/core';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { Request } from '../../codegen/graphql';
import { GET_AD_ZONE_STORES_FOR_REQUEST } from '../../apollo/queries/priceChangeRequest';
import { ProductSalesWeekItem } from '../../helpers/groupProductSalesByLast4Weeks';
import {
    getPriceChangeScope,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
} from '../../helpers/priceChangeScope';
import { showErrorNotification } from '../../notifications';
import formatCurrency from '../../utility/formatCurrency';
import { useProductSalesScope } from '../../hooks/useProductSalesScope';
import type { UseProductSalesScopeResult } from '../../hooks/useProductSalesScope';
import { useProductSalesWindows } from '../../hooks/useProductSalesWindows';
import { useSalesDataMaintenance } from '../../hooks/useSalesDataMaintenance';
import SalesDataMaintenanceIllustration from '../../assets/illustrations/product-sales-maintenance.png';
import { PropagationStopper, When } from '../common';
import { ProductSalesChartLegend } from './ProductSalesChartLegend';
import { ProductSalesChartSkeleton } from './ProductSalesChartSkeleton';
import { ProductSalesSummaryMetrics } from './ProductSalesSummaryMetrics';
import ProductSalesScopeControl from './ProductSalesScopeControl';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

const THIS_YEAR_COLOR = '#77E1DB';
const LAST_YEAR_COLOR = '#2A5EA7';

type ChartView = 'line' | 'bar';
type ChartMetric = 'units' | 'value';

function formatChartValue(
    value: number,
    metric: ChartMetric,
    currency: string,
): string {
    if (metric === 'value') {
        return formatCurrency(value, currency);
    }
    return `${value.toLocaleString()} units`;
}

function buildLineOptions(
    metric: ChartMetric,
    currency: string,
): ChartOptions<'line'> {
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label(context) {
                        const series = context.dataset.label || '';
                        if (context.parsed.y === null) {
                            return `${series}: —`;
                        }
                        return `${series}: ${formatChartValue(
                            context.parsed.y,
                            metric,
                            currency,
                        )}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#7D8FB3',
                    font: {
                        size: 11,
                    },
                },
                border: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#EDEFF2',
                },
                ticks: {
                    color: '#7D8FB3',
                    font: {
                        size: 11,
                    },
                    callback(tickValue) {
                        const numeric =
                            typeof tickValue === 'string'
                                ? Number(tickValue)
                                : tickValue;
                        if (Number.isNaN(numeric)) {
                            return String(tickValue);
                        }
                        if (metric === 'value') {
                            return formatCurrency(numeric, currency);
                        }
                        return numeric.toLocaleString();
                    },
                },
                border: {
                    display: false,
                },
            },
        },
    };
}

function buildBarOptions(
    metric: ChartMetric,
    currency: string,
): ChartOptions<'bar'> {
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        datasets: {
            bar: {
                categoryPercentage: 0.55,
                barPercentage: 0.95,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label(context) {
                        const series = context.dataset.label || '';
                        if (context.parsed.y === null) {
                            return `${series}: —`;
                        }
                        return `${series}: ${formatChartValue(
                            context.parsed.y,
                            metric,
                            currency,
                        )}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#7D8FB3',
                    font: {
                        size: 11,
                    },
                },
                border: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#EDEFF2',
                },
                ticks: {
                    color: '#7D8FB3',
                    font: {
                        size: 11,
                    },
                    callback(tickValue) {
                        const numeric =
                            typeof tickValue === 'string'
                                ? Number(tickValue)
                                : tickValue;
                        if (Number.isNaN(numeric)) {
                            return String(tickValue);
                        }
                        if (metric === 'value') {
                            return formatCurrency(numeric, currency);
                        }
                        return numeric.toLocaleString();
                    },
                },
                border: {
                    display: false,
                },
            },
        },
    };
}

type SalesTotals = {
    totalQuantity: number;
    totalValue: number;
};

type WeeklyChartSeries = {
    labels: string[];
    thisYearQuantities: number[];
    lastYearQuantities: number[];
    thisYearValues: number[];
    lastYearValues: number[];
};

function sumWeeklySeriesTotals(
    quantities: number[],
    values: number[],
): SalesTotals {
    return {
        totalQuantity: quantities.reduce((acc, value) => acc + value, 0),
        totalValue: values.reduce((acc, value) => acc + value, 0),
    };
}

function totalsInWeek(
    sales: ProductSalesWeekItem[],
    start: Date,
    end: Date,
): { totalQuantity: number; totalValue: number } {
    const from = dayjs(start).startOf('day');
    const to = dayjs(end).startOf('day');
    const matched = sales.filter(item => {
        const date = dayjs(item.date).startOf('day');
        return !date.isBefore(from) && !date.isAfter(to);
    });
    return {
        totalQuantity: matched.reduce(
            (total, item) => total + item.totalQuantity,
            0,
        ),
        totalValue: matched.reduce(
            (total, item) => total + (item.totalValue ?? 0),
            0,
        ),
    };
}

function buildWeeklyChartSeries(
    thisYearSales: ProductSalesWeekItem[],
    lastYearSales: ProductSalesWeekItem[],
    thisYearWeeks: { start: Date; end: Date; weekNumber: number }[],
    lastYearWeeks: { start: Date; end: Date; weekNumber: number }[],
): WeeklyChartSeries {
    const labels: string[] = [];
    const thisYearQuantities: number[] = [];
    const lastYearQuantities: number[] = [];
    const thisYearValues: number[] = [];
    const lastYearValues: number[] = [];

    for (let weekIndex = 0; weekIndex < thisYearWeeks.length; weekIndex += 1) {
        const thisYearWeek = thisYearWeeks[weekIndex];
        const lastYearWeek = lastYearWeeks[weekIndex];
        if (thisYearWeek && lastYearWeek) {
            const thisYear = totalsInWeek(
                thisYearSales,
                thisYearWeek.start,
                thisYearWeek.end,
            );
            const lastYear = totalsInWeek(
                lastYearSales,
                lastYearWeek.start,
                lastYearWeek.end,
            );
            labels.push(`FW${thisYearWeek.weekNumber}`);
            thisYearQuantities.push(thisYear.totalQuantity);
            lastYearQuantities.push(lastYear.totalQuantity);
            thisYearValues.push(thisYear.totalValue);
            lastYearValues.push(lastYear.totalValue);
        }
    }

    return {
        labels,
        thisYearQuantities,
        lastYearQuantities,
        thisYearValues,
        lastYearValues,
    };
}

type Props = {
    request: Request | null;
    salesScope?: UseProductSalesScopeResult;
    layout?: 'default' | 'compact';
    showChart?: boolean;
};

function ProductSalesLineGraph({
    request,
    salesScope: salesScopeProp,
    layout = 'default',
    showChart = true,
}: Props) {
    const [chartView, setChartView] = useState<ChartView>('line');
    const [chartMetric, setChartMetric] = useState<ChartMetric>('units');
    const [chartVisible, setChartVisible] = useState(true);

    const salesMaintenance = useSalesDataMaintenance();
    const localSalesScope = useProductSalesScope({
        advertisingZoneId: request?.advertisingZoneId,
        priceChangeScope: getPriceChangeScope(request),
        suggestedStoreIds: getSuggestedStoreIds(request),
    });
    const salesScope = salesScopeProp ?? localSalesScope;

    const {
        thisYearSales,
        lastYearSales,
        loading,
        error,
        hasSalesSource,
        showWarmingHint,
        showMaintenanceNotice,
        storeCount,
        hasMixedCurrencies,
        thisYearWeekLabel,
        lastYearWeekLabel,
        thisYearWeeks,
        lastYearWeeks,
    } = useProductSalesWindows({
        productId: request?.productId,
        requestCreatedAt: request?.createdAt,
        skip: !request?.productId || !request?.createdAt,
        scopeInput: salesScope.scopeInput,
    });

    const { data: adZoneStoresData } = useQuery(
        GET_AD_ZONE_STORES_FOR_REQUEST,
        {
            skip: !request?.id || salesScope.scope !== 'wide',
            fetchPolicy: 'cache-first',
            variables: {
                requestId: request?.id,
                productSku: request?.productSku,
            },
        },
    );

    const scopeStores = useMemo(() => {
        const zoneStores =
            adZoneStoresData?.getAdvertisingZoneStoresForRequest ?? [];
        const liveOpenStores = zoneStores.filter(
            store => !store.isClosed && store.productAvailability?.isLive,
        );
        const priceChangeScope = getPriceChangeScope(request);
        if (priceChangeScope !== PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
            return liveOpenStores;
        }

        const suggestedStoreIds = new Set(getSuggestedStoreIds(request));
        if (suggestedStoreIds.size === 0) {
            return liveOpenStores;
        }

        return liveOpenStores.filter(store => suggestedStoreIds.has(store.id));
    }, [adZoneStoresData, request]);

    const currency =
        request?.currentPriceCurrency || request?.currency || 'ZAR';

    const weeklySeries = useMemo(() => {
        if (!hasSalesSource || thisYearWeeks.length === 0) {
            return null;
        }
        return buildWeeklyChartSeries(
            thisYearSales,
            lastYearSales,
            thisYearWeeks,
            lastYearWeeks,
        );
    }, [
        hasSalesSource,
        thisYearSales,
        lastYearSales,
        thisYearWeeks,
        lastYearWeeks,
    ]);

    const salesSummary = useMemo(() => {
        if (!weeklySeries) {
            return {
                thisYear: { totalQuantity: 0, totalValue: 0 },
                lastYear: { totalQuantity: 0, totalValue: 0 },
                quantityDiff: 0,
                valueDiff: 0,
            };
        }

        const thisYear = sumWeeklySeriesTotals(
            weeklySeries.thisYearQuantities,
            weeklySeries.thisYearValues,
        );
        const lastYear = sumWeeklySeriesTotals(
            weeklySeries.lastYearQuantities,
            weeklySeries.lastYearValues,
        );

        return {
            thisYear,
            lastYear,
            quantityDiff: thisYear.totalQuantity - lastYear.totalQuantity,
            valueDiff: thisYear.totalValue - lastYear.totalValue,
        };
    }, [weeklySeries]);

    const activeThisYearValues = useMemo(() => {
        if (!weeklySeries) {
            return [];
        }
        return chartMetric === 'units'
            ? weeklySeries.thisYearQuantities
            : weeklySeries.thisYearValues;
    }, [weeklySeries, chartMetric]);

    const activeLastYearValues = useMemo(() => {
        if (!weeklySeries) {
            return [];
        }
        return chartMetric === 'units'
            ? weeklySeries.lastYearQuantities
            : weeklySeries.lastYearValues;
    }, [weeklySeries, chartMetric]);

    const lineOptions = useMemo(
        () => buildLineOptions(chartMetric, currency),
        [chartMetric, currency],
    );

    const barOptions = useMemo(
        () => buildBarOptions(chartMetric, currency),
        [chartMetric, currency],
    );

    const lineData = useMemo(() => {
        if (!weeklySeries) {
            return {
                labels: [],
                datasets: [],
            };
        }

        return {
            labels: weeklySeries.labels,
            datasets: [
                {
                    label: thisYearWeekLabel || 'This Year',
                    data: activeThisYearValues,
                    borderColor: THIS_YEAR_COLOR,
                    tension: 0.35,
                    backgroundColor: (ctx: ScriptableChartContext) => {
                        const gradient = ctx.chart.ctx.createLinearGradient(
                            0,
                            0,
                            0,
                            400,
                        );
                        gradient.addColorStop(0, '#77E1DB70');
                        gradient.addColorStop(1, '#FFFFFF10');
                        return gradient;
                    },
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    pointBackgroundColor: THIS_YEAR_COLOR,
                    fill: true,
                },
                {
                    label: lastYearWeekLabel || 'Last Year',
                    data: activeLastYearValues,
                    borderColor: LAST_YEAR_COLOR,
                    tension: 0.35,
                    backgroundColor: (ctx: ScriptableChartContext) => {
                        const gradient = ctx.chart.ctx.createLinearGradient(
                            0,
                            0,
                            0,
                            400,
                        );
                        gradient.addColorStop(0, '#2A5EA770');
                        gradient.addColorStop(1, '#FFFFFF10');
                        return gradient;
                    },
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    pointBackgroundColor: LAST_YEAR_COLOR,
                    fill: true,
                },
            ],
        };
    }, [
        weeklySeries,
        activeThisYearValues,
        activeLastYearValues,
        thisYearWeekLabel,
        lastYearWeekLabel,
    ]);

    const barData = useMemo(() => {
        if (!weeklySeries) {
            return {
                labels: [],
                datasets: [],
            };
        }

        return {
            labels: weeklySeries.labels,
            datasets: [
                {
                    label: thisYearWeekLabel || 'This Year',
                    data: activeThisYearValues,
                    backgroundColor: THIS_YEAR_COLOR,
                    borderColor: THIS_YEAR_COLOR,
                    borderRadius: 4,
                    borderSkipped: false,
                },
                {
                    label: lastYearWeekLabel || 'Last Year',
                    data: activeLastYearValues,
                    backgroundColor: LAST_YEAR_COLOR,
                    borderColor: LAST_YEAR_COLOR,
                    borderRadius: 4,
                    borderSkipped: false,
                },
            ],
        };
    }, [
        weeklySeries,
        activeThisYearValues,
        activeLastYearValues,
        thisYearWeekLabel,
        lastYearWeekLabel,
    ]);

    useEffect(() => {
        if (error) {
            showErrorNotification('Unable to fetch product sales');
        }
    }, [error]);

    useEffect(() => {
        if (hasMixedCurrencies) {
            setChartMetric('units');
        }
    }, [hasMixedCurrencies]);

    const handleChartViewChange = (value: string) => {
        const nextView = value as ChartView;
        if (nextView === chartView) {
            return;
        }
        setChartVisible(false);
        window.setTimeout(() => {
            setChartView(nextView);
            setChartVisible(true);
        }, 140);
    };

    const handleMetricChange = (value: string) => {
        setChartMetric(value as ChartMetric);
    };

    const hasChartData = thisYearSales.length > 0 || lastYearSales.length > 0;

    const emptyState = (
        <div className="flex h-48 items-center justify-center">
            <Text className="text-center text-sm font-medium text-[#4D5E80]">
                No Historical Product Sales
            </Text>
        </div>
    );

    const chartContent = (
        <PropagationStopper className="flex flex-col gap-4">
            <ProductSalesSummaryMetrics
                lastYearQuantity={salesSummary.lastYear.totalQuantity}
                lastYearValue={salesSummary.lastYear.totalValue}
                thisYearQuantity={salesSummary.thisYear.totalQuantity}
                thisYearValue={salesSummary.thisYear.totalValue}
                quantityDiff={salesSummary.quantityDiff}
                valueDiff={salesSummary.valueDiff}
                currency={currency}
                hideValue={hasMixedCurrencies}
            />

            <When condition={showChart}>
                <div className="flex flex-col gap-4">
                    <div
                        className={
                            layout === 'compact'
                                ? 'flex flex-col gap-3'
                                : 'flex flex-wrap items-center justify-between gap-3'
                        }
                    >
                        <ProductSalesChartLegend
                            thisYearLabel={thisYearWeekLabel || 'This Year'}
                            lastYearLabel={lastYearWeekLabel || 'Last Year'}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                            <When
                                condition={hasMixedCurrencies}
                                fallback={
                                    <SegmentedControl
                                        value={chartMetric}
                                        onChange={handleMetricChange}
                                        data={[
                                            { label: 'Units', value: 'units' },
                                            { label: currency, value: 'value' },
                                        ]}
                                        color="primary"
                                        size="xs"
                                        radius="md"
                                        className="shrink-0"
                                    />
                                }
                            >
                                <MantineTooltip label="Zone sales span multiple currencies, so value totals are hidden">
                                    <SegmentedControl
                                        value="units"
                                        data={[
                                            { label: 'Units', value: 'units' },
                                        ]}
                                        color="primary"
                                        size="xs"
                                        radius="md"
                                        className="shrink-0"
                                    />
                                </MantineTooltip>
                            </When>
                            <SegmentedControl
                                value={chartView}
                                onChange={handleChartViewChange}
                                data={[
                                    { label: 'Line', value: 'line' },
                                    { label: 'Bar', value: 'bar' },
                                ]}
                                color="primary"
                                size="xs"
                                radius="md"
                                className="shrink-0"
                            />
                        </div>
                    </div>

                    <div
                        className={`h-56 w-full min-w-0 ${
                            chartVisible ? 'opacity-100' : 'opacity-0'
                        }`}
                    >
                        <When
                            condition={chartView === 'line'}
                            fallback={
                                <Bar options={barOptions} data={barData} />
                            }
                        >
                            <Line options={lineOptions} data={lineData} />
                        </When>
                    </div>
                </div>
            </When>

            <When condition={showWarmingHint}>
                <Text size="xs" c="dimmed">
                    Fetching remaining zone data…
                </Text>
            </When>
        </PropagationStopper>
    );

    if (showMaintenanceNotice) {
        return (
            <Flex direction="column" align="center" className="py-6">
                <Image
                    src={SalesDataMaintenanceIllustration}
                    alt="Sales data maintenance"
                    w={160}
                    h={160}
                    fit="contain"
                    className="mb-2"
                />
                <Text size="sm" c="dimmed" className="max-w-sm text-center">
                    {salesMaintenance.message}
                </Text>
            </Flex>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <ProductSalesScopeControl
                variant="section"
                scope={salesScope.scope}
                setScope={salesScope.setScope}
                canToggle={salesScope.canToggle}
                wideLabel={salesScope.wideLabel}
                storeCount={storeCount}
                stores={scopeStores}
            />
            <Divider color="#EDEFF2" />
            <When
                condition={loading}
                fallback={
                    <When condition={hasChartData} fallback={emptyState}>
                        {chartContent}
                    </When>
                }
            >
                <PropagationStopper>
                    <ProductSalesChartSkeleton showChart={showChart} />
                </PropagationStopper>
            </When>
        </div>
    );
}

export default ProductSalesLineGraph;
